import { useEffect, useState, useCallback } from 'react';
import {
  Smartphone,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  PowerOff,
} from 'lucide-react';
import { pushAPI, type PushDevice } from '../../services/api';

export default function DispositivosTab() {
  const [items, setItems] = useState<PushDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [cidade, setCidade] = useState('');
  const [bairro, setBairro] = useState('');
  const [userId, setUserId] = useState('');
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('true');
  const [deactivating, setDeactivating] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await pushAPI.listDevices({
        page,
        limit: 20,
        cidade: cidade.trim() || undefined,
        bairro: bairro.trim() || undefined,
        userId: userId.trim() || undefined,
        active: activeFilter === '' ? undefined : activeFilter === 'true',
      });
      setItems(r.data);
      setTotalPages(r.pagination.pages || 1);
      setTotal(r.pagination.total);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message ?? e.message ?? 'Erro ao carregar dispositivos.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, cidade, bairro, userId, activeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [cidade, bairro, userId, activeFilter]);

  async function handleDeactivate(d: PushDevice) {
    if (!confirm(`Desativar device ${d.deviceId} (${d.userId})?`)) return;
    setDeactivating(d.id);
    try {
      await pushAPI.deactivateDevice(d.id);
      await load();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert(e.response?.data?.message ?? e.message ?? 'Erro ao desativar.');
    } finally {
      setDeactivating(null);
    }
  }

  function fmt(iso?: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return (
      d.toLocaleDateString('pt-BR') +
      ' ' +
      d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    );
  }

  function platformBadge(p: string): string {
    if (p === 'ios') return 'bg-hover text-fg-2 border border-active';
    if (p === 'android') return 'bg-success-soft/30 text-success border border-success-line/50';
    return 'bg-primary-soft/30 text-primary border border-primary-line/50';
  }

  return (
    <div className="bg-surface border border-line rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-fg flex items-center gap-2">
            <Smartphone size={18} className="text-primary" />
            Dispositivos registrados
          </h2>
          <p className="text-sm text-fg-2 mt-1">
            {total} device(s) {activeFilter === 'true' ? 'ativos' : activeFilter === 'false' ? 'inativos' : 'no total'}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-hover hover:bg-active disabled:opacity-50 text-fg rounded-lg text-sm transition"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <input
          type="text"
          value={cidade}
          onChange={(e) => setCidade(e.target.value)}
          placeholder="Cidade..."
          className="px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
        />
        <input
          type="text"
          value={bairro}
          onChange={(e) => setBairro(e.target.value)}
          placeholder="Bairro..."
          className="px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
        />
        <input
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="CPF/CNPJ..."
          className="px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
        />
        <select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as '' | 'true' | 'false')}
          className="px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm focus:border-primary focus:outline-none"
        >
          <option value="true">Apenas ativos</option>
          <option value="false">Apenas inativos</option>
          <option value="">Todos</option>
        </select>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 mb-4 bg-danger-soft/20 border border-danger-line/50 rounded-lg text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Tabela */}
      <div className="border border-line rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas">
            <tr className="text-left text-xs text-muted uppercase tracking-wide">
              <th className="px-3 py-3">CPF/CNPJ</th>
              <th className="px-3 py-3">Device</th>
              <th className="px-3 py-3">Plataforma</th>
              <th className="px-3 py-3">Região</th>
              <th className="px-3 py-3">Última atividade</th>
              <th className="px-3 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {loading && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-muted">
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-muted">
                  Nenhum dispositivo encontrado.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((d) => (
                <tr
                  key={d.id}
                  className={`hover:bg-canvas/50 ${!d.active ? 'opacity-50' : ''}`}
                >
                  <td className="px-3 py-3 text-fg-2 font-mono text-xs">{d.userId}</td>
                  <td className="px-3 py-3 text-fg-2">
                    <div className="text-xs font-mono">{d.deviceId}</div>
                    {d.deviceModel && (
                      <div className="text-xs text-muted">{d.deviceModel}</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs ${platformBadge(
                        d.platform,
                      )}`}
                    >
                      {d.platform}
                    </span>
                    <div className="text-xs text-muted mt-1">{d.tokenType}</div>
                  </td>
                  <td className="px-3 py-3 text-fg-2 text-xs">
                    {d.cidade || '—'}
                    {d.bairro && <span className="text-faint"> / {d.bairro}</span>}
                  </td>
                  <td className="px-3 py-3 text-fg-2 text-xs">{fmt(d.lastActive)}</td>
                  <td className="px-3 py-3">
                    {d.active ? (
                      <button
                        type="button"
                        onClick={() => handleDeactivate(d)}
                        disabled={deactivating === d.id}
                        className="flex items-center gap-1.5 px-2 py-1 bg-danger-soft/20 hover:bg-danger-soft/40 border border-danger-line/50 text-danger rounded text-xs transition disabled:opacity-50"
                      >
                        <PowerOff size={12} />
                        {deactivating === d.id ? 'Desativando…' : 'Desativar'}
                      </button>
                    ) : (
                      <span className="text-xs text-faint">inativo</span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-muted">
            Página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-2 bg-hover hover:bg-active disabled:opacity-50 text-fg rounded-lg transition"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="p-2 bg-hover hover:bg-active disabled:opacity-50 text-fg rounded-lg transition"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
